import { Alert, Button } from '@fountain/fountain-ui-components';
import { Chip, Grid, TextField, Typography } from '@material-ui/core';
import cx from 'classnames';
import React, { useState } from 'react';
import { FormattedMessage, MessageDescriptor, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import { REACT_APP_MONOLITH_BASE_URL } from 'runtimeEnvVars';

import { apiPost } from 'utils/axios';

import { messages } from './messages';
import { useStyles } from './styles';

type Verdict =
  | 'eligible'
  | 'not_eligible'
  | 'no_match'
  | 'ambiguous'
  | 'unavailable'
  | 'field_not_mapped';

interface DryRunEmployee {
  employmentStatus?: string | null;
  terminationDate?: string | null;
  changedAt?: string | null;
  customFields?: Record<string, unknown> | null;
}

interface DryRunLookupResult {
  outcome: 'match' | 'no_match' | 'ambiguous' | 'unavailable';
  reason?: string | null;
  employee?: DryRunEmployee | null;
}

interface DryRunApiResponse {
  data: {
    result: DryRunLookupResult;
  };
}

export interface DryRunTesterProps {
  allowSsn: boolean;
  connectorUuid: string;
  eligibilityField: string;
  eligibleValues: string[];
}

// Mirrors RehireCheckStages::LookupJob#derive_match_verdict so the dry-run tester reflects the
// panel's *current unsaved* connector/eligibility-field/eligible-values config, not whatever is
// currently persisted on the stage.
function deriveVerdict(
  result: DryRunLookupResult,
  eligibilityField: string,
  eligibleValues: string[],
): Verdict {
  if (result.outcome !== 'match') {
    return result.outcome;
  }

  const rawValue = eligibilityField
    ? result.employee?.customFields?.[eligibilityField]
    : undefined;
  const normalizedRawValue = rawValue?.toString().trim();

  if (!normalizedRawValue) {
    return 'field_not_mapped';
  }

  const normalized = normalizedRawValue.toLowerCase();
  const isEligible = eligibleValues.some(
    value => value.trim().toLowerCase() === normalized,
  );

  return isEligible ? 'eligible' : 'not_eligible';
}

function verdictMessage(verdict: Verdict): MessageDescriptor {
  switch (verdict) {
    case 'eligible':
      return messages.verdictEligible;
    case 'not_eligible':
      return messages.verdictNotEligible;
    case 'no_match':
      return messages.verdictNoMatch;
    case 'ambiguous':
      return messages.verdictAmbiguous;
    case 'field_not_mapped':
      return messages.verdictFieldNotMapped;
    case 'unavailable':
    default:
      return messages.verdictUnavailable;
  }
}

function verdictClassName(
  styles: ReturnType<typeof useStyles>,
  verdict: Verdict,
): string {
  switch (verdict) {
    case 'eligible':
      return styles.verdictPillEligible;
    case 'not_eligible':
      return styles.verdictPillNotEligible;
    case 'no_match':
      return styles.verdictPillNeutral;
    case 'ambiguous':
    case 'unavailable':
    case 'field_not_mapped':
    default:
      return styles.verdictPillWarning;
  }
}

function verdictDetailMessage(
  verdict: Verdict,
  reason: string | null | undefined,
): MessageDescriptor | null {
  if (verdict === 'field_not_mapped') {
    return messages.verdictFieldNotMappedExplanation;
  }

  if (verdict === 'unavailable') {
    return reason === 'no_connector'
      ? messages.verdictUnavailableNoConnector
      : messages.verdictUnavailableLookupError;
  }

  return null;
}

export function DryRunTester({
  allowSsn,
  connectorUuid,
  eligibilityField,
  eligibleValues,
}: DryRunTesterProps) {
  const styles = useStyles();
  const intl = useIntl();
  const { funnelSlug } = useParams<{ funnelSlug?: string }>();

  const [email, setEmail] = useState('');
  const [ssn, setSsn] = useState('');
  const [isRunning, setIsRunning] = useState(false);
  const [result, setResult] = useState<DryRunLookupResult | null>(null);
  const [requestFailed, setRequestFailed] = useState(false);

  const canRunTest = Boolean(email.trim() && funnelSlug) && !isRunning;

  const runTest = () => {
    setIsRunning(true);
    setRequestFailed(false);
    setResult(null);

    apiPost(
      `${REACT_APP_MONOLITH_BASE_URL}/internal_api/workflow_editor/rehire_check/dry_run`,
      {
        connector_uuid: connectorUuid || undefined,
        eligibility_field: eligibilityField || undefined,
        email,
        funnel_slug: funnelSlug,
        ...(allowSsn && ssn ? { ssn } : {}),
      },
    )
      .then((response: DryRunApiResponse) => {
        setResult(response.data.result);
      })
      .catch(() => {
        setRequestFailed(true);
      })
      .finally(() => {
        setIsRunning(false);
      });
  };

  const verdict = result
    ? deriveVerdict(result, eligibilityField, eligibleValues)
    : null;
  const rawValue = eligibilityField
    ? result?.employee?.customFields?.[eligibilityField]
    : undefined;
  const detailMessage = verdict
    ? verdictDetailMessage(verdict, result?.reason)
    : null;

  return (
    <div className={styles.subCard}>
      <Typography variant="h4" className={styles.subCardTitle}>
        <FormattedMessage {...messages.dryRunCardTitle} />
      </Typography>
      <Typography variant="body2" className={styles.subCardDescription}>
        <FormattedMessage {...messages.dryRunDescription} />
      </Typography>

      <Grid container className={styles.dryRunFieldsRow}>
        <Grid item xs={7}>
          <Typography variant="body2" className={styles.fieldLabel}>
            <FormattedMessage {...messages.dryRunEmailLabel} />
          </Typography>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            type="email"
            value={email}
            onChange={event => setEmail(event.target.value)}
            inputProps={{
              'aria-label': intl.formatMessage(messages.dryRunEmailLabel),
            }}
          />
        </Grid>
        {allowSsn && (
          <Grid item xs={5}>
            <Typography variant="body2" className={styles.fieldLabel}>
              <FormattedMessage {...messages.dryRunSsnLabel} />
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              value={ssn}
              onChange={event => setSsn(event.target.value)}
              inputProps={{
                'aria-label': intl.formatMessage(messages.dryRunSsnLabel),
              }}
            />
          </Grid>
        )}
      </Grid>

      <div className={styles.dryRunButtonContainer}>
        <Button
          type="secondary"
          isLoading={isRunning}
          disabled={!canRunTest}
          onClick={runTest}
        >
          <FormattedMessage {...messages.dryRunButton} />
        </Button>
      </div>

      {requestFailed && (
        <Alert type="danger" fullWidth className={styles.dryRunResult}>
          <FormattedMessage {...messages.dryRunErrorMessage} />
        </Alert>
      )}

      {verdict && result && (
        <div className={styles.dryRunResult} data-testid="dry-run-result">
          <Chip
            label={intl.formatMessage(verdictMessage(verdict))}
            className={cx(
              styles.verdictPill,
              verdictClassName(styles, verdict),
            )}
          />

          {detailMessage && (
            <Typography variant="body2" className={styles.resultDetailRow}>
              <FormattedMessage {...detailMessage} />
            </Typography>
          )}

          {rawValue !== undefined && (
            <Typography variant="body2" className={styles.resultDetailRow}>
              <span className={styles.resultDetailLabel}>
                <FormattedMessage {...messages.dryRunResultRawValueLabel} />
                {': '}
              </span>
              {rawValue?.toString() || '—'}
            </Typography>
          )}

          {result.employee?.employmentStatus && (
            <Typography variant="body2" className={styles.resultDetailRow}>
              <span className={styles.resultDetailLabel}>
                <FormattedMessage {...messages.dryRunResultStatusLabel} />
                {': '}
              </span>
              {result.employee.employmentStatus}
            </Typography>
          )}

          {result.employee?.terminationDate && (
            <Typography variant="body2" className={styles.resultDetailRow}>
              <span className={styles.resultDetailLabel}>
                <FormattedMessage
                  {...messages.dryRunResultTerminationDateLabel}
                />
                {': '}
              </span>
              {result.employee.terminationDate}
            </Typography>
          )}

          {result.employee?.changedAt && (
            <Typography variant="body2" className={styles.resultDetailRow}>
              <span className={styles.resultDetailLabel}>
                <FormattedMessage {...messages.dryRunResultDataAsOfLabel} />
                {': '}
              </span>
              {result.employee.changedAt}
            </Typography>
          )}
        </div>
      )}
    </div>
  );
}
